// src/components/GuestDrawer.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Drawer,
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  Checkbox,
  InputNumber,
  Typography,
  Space,
  Tooltip,
  ConfigProvider,
  message,
  Select,
} from "antd";
import { SearchOutlined, CloseOutlined, CheckOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import "./css.css";

import ReactCountryFlag from "react-country-flag";

// Optional (recommended) enhanced validation:
// npm i libphonenumber-js
import { parsePhoneNumber } from "libphonenumber-js";

const { Text } = Typography;

/* ───────────────── Country / Dial Codes ───────────────── */

const COUNTRY_LIST = [
  { name: "Botswana", dialCode: "+267", code: "BW" },
  { name: "South Africa", dialCode: "+27", code: "ZA" },
  { name: "Zambia", dialCode: "+260", code: "ZM" },
  { name: "Zimbabwe", dialCode: "+263", code: "ZW" },
  { name: "Namibia", dialCode: "+264", code: "NA" },
  { name: "Mozambique", dialCode: "+258", code: "MZ" },
  { name: "Kenya", dialCode: "+254", code: "KE" },
  { name: "Nigeria", dialCode: "+234", code: "NG" },
  { name: "Ghana", dialCode: "+233", code: "GH" },
  { name: "United States", dialCode: "+1", code: "US" },
];

const LOCAL_LENGTHS = {
  BW: 8,
  ZA: 9,
  ZM: 9,
  ZW: 9,
  NA: 8,
  MZ: 8,
  KE: 9,
  NG: 10,
  GH: 9,
  US: 10,
};

const digitsOnly = (v) => (v || "").replace(/\D/g, "");

/**
 * Find the best matching country by dial code prefix (longest match).
 * Ex: +267... => BW
 */
const findCountryFromValue = (value) => {
  const v = (value || "").trim();
  if (!v.startsWith("+")) return null;

  const sorted = [...COUNTRY_LIST].sort((a, b) => b.dialCode.length - a.dialCode.length);
  return sorted.find((c) => v.startsWith(c.dialCode)) || null;
};

const splitE164 = (value, fallbackCountry) => {
  const v = (value || "").trim();
  const matched = findCountryFromValue(v);

  const country = matched || fallbackCountry || COUNTRY_LIST[0];
  if (!v) return { country, local: "" };

  if (matched) {
    const local = v.slice(matched.dialCode.length);
    return { country: matched, local: digitsOnly(local) };
  }

  // no "+" => treat as local digits, keep fallback country
  return { country, local: digitsOnly(v) };
};

// Enhanced phone validation (libphonenumber-js first, fallback lengths second)
const validatePhone = (dialCode, number, isoCode) => {
  const local = digitsOnly(number);
  if (!local) return false;

  try {
    const phone = parsePhoneNumber(`${dialCode}${local}`);
    if (phone?.isValid?.()) return true;
  } catch (_) {
    // fall through
  }

  const expectedLength = LOCAL_LENGTHS[isoCode];
  if (expectedLength) return local.length === expectedLength;

  return local.length >= 6 && local.length <= 15;
};

/* ───────────────── Reusable Form Control ───────────────── */

/**
 * Form-friendly control:
 * - value stored as E.164 (+26771928306)
 * - UI: [Select dial code + flag] [local digits]
 * - If user pastes +E.164 into the input, it auto-detects and switches country.
 *
 * ✅ NEW:
 * - If API/DB loads a local-only number (no "+"), this component auto-normalizes it
 *   back to E.164 by calling onChange(+dialCode + digits).
 */
const CountryPhoneInput = ({
  value,
  onChange,
  placeholder = "Number",
  defaultDialCode = "+267",
}) => {
  const fallbackCountry =
    COUNTRY_LIST.find((c) => c.dialCode === defaultDialCode) || COUNTRY_LIST[0];

  const [{ country, local }, setState] = useState(() => splitE164(value, fallbackCountry));

  useEffect(() => {
    const next = splitE164(value, fallbackCountry);
    setState(next);

    // ✅ Auto-normalize local-only values into E.164 (prevents backend E.164 validator errors)
    const raw = (value || "").trim();
    if (raw && !raw.startsWith("+")) {
      const normalized = next.local ? `${next.country.dialCode}${next.local}` : "";
      if (normalized && normalized !== raw) {
        onChange?.(normalized);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultDialCode]);

  const emit = useCallback(
    (nextCountry, nextLocal) => {
      const l = digitsOnly(nextLocal);
      const full = l ? `${nextCountry.dialCode}${l}` : "";
      onChange?.(full);
    },
    [onChange]
  );

  const dialOptions = useMemo(() => {
    return COUNTRY_LIST.map((c) => ({
      value: c.dialCode,
      searchText: `${c.dialCode} ${c.name} ${c.code}`.toLowerCase(),
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ReactCountryFlag
            countryCode={c.code}
            svg
            style={{ width: "1.1em", height: "1.1em", borderRadius: 2 }}
            title={c.name}
          />
          <span style={{ fontWeight: 600 }}>{c.dialCode}</span>
          <span style={{ opacity: 0.7 }}>{c.name}</span>
        </span>
      ),
    }));
  }, []);

  const handleLocalChange = (raw) => {
    const nextRaw = raw ?? "";
    const trimmed = nextRaw.trim();

    // If user pastes +E.164, auto-detect
    if (trimmed.startsWith("+")) {
      const matched = findCountryFromValue(trimmed);
      if (matched) {
        const nextLocal = digitsOnly(trimmed.slice(matched.dialCode.length));
        setState({ country: matched, local: nextLocal });
        emit(matched, nextLocal);
        return;
      }
    }

    setState((prev) => ({ ...prev, local: nextRaw }));
    emit(country, nextRaw);
  };

  return (
    <Input
      value={local}
      onChange={(e) => handleLocalChange(e.target.value)}
      inputMode="tel"
      placeholder={placeholder}
      addonBefore={
        <Select
          value={country?.dialCode}
          options={dialOptions}
          showSearch
          filterOption={(input, option) => {
            const needle = (input || "").toLowerCase();
            return (option?.searchText || "").includes(needle);
          }}
          onChange={(dial) => {
            const nextCountry = COUNTRY_LIST.find((c) => c.dialCode === dial) || country;
            setState((prev) => ({ ...prev, country: nextCountry }));
            emit(nextCountry, local);
          }}
          style={{ width: 190 }}
        />
      }
      allowClear
      onBlur={() => {
        // normalize to digits only on blur
        const clean = digitsOnly(local);
        if (clean !== local) {
          setState((prev) => ({ ...prev, local: clean }));
          emit(country, clean);
        }
      }}
    />
  );
};

/* ───────────────── GuestDrawer ───────────────── */

const GuestDrawer = ({ open, onClose, form }) => {
  const { authData } = useAuth();

  const eighteenYearsAgo = useMemo(() => dayjs().subtract(18, "year").endOf("day"), []);
  const includesKids = Form.useWatch("includesKids", form);
  const idNumber = Form.useWatch("idNumber", form);

  const namePreview = useMemo(() => {
    const f = (form.getFieldValue("firstName") || "").trim();
    const l = (form.getFieldValue("lastName") || "").trim();
    return [f, l].filter(Boolean).join(" ");
  }, [form, idNumber]);

  // Default dial code: use from auth/hotel if available, else Botswana
  const defaultDialCode = useMemo(() => {
    return authData?.countryDialCode || authData?.hotelCountryDialCode || "+267";
  }, [authData]);

  const handleGuestSearch = async () => {
    const id = form.getFieldValue("idNumber")?.trim();
    if (!id) return message.warning("Enter ID/Passport number to search");

    try {
      const { data: guest } = await axiosInstance.get("/Guests/SearchByIdNumberAndHotel2", {
        params: { idNumber: id, hotelId: authData?.hotelId },
      });

      if (guest) {
        form.setFieldsValue({
          idNumber: guest.idNumber || id,
          firstName: guest.firstName || "",
          lastName: guest.lastName || "",

          // ✅ Email is OPTIONAL now (leave empty allowed)
          email: guest.email || "",

          // ✅ Rename: company -> companyName (matches BookingCreateDTO)
          companyName: guest.company || guest.companyName || "",
          companyEmail: guest.companyEmail || "",

          mobileNumber: guest.mobileNumber || "",
          phoneNumber: guest.phoneNumber || "",

          // addresses
          postalAddress: guest.postalAddress || guest.address || "",
          residentialAddress: guest.residentialAddress || "",

          dateOfBirth: guest.dateOfBirth ? dayjs(guest.dateOfBirth) : null,
          guestId: guest.guestId || null,
        });
        message.success("Guest found and loaded");
      } else {
        form.setFieldsValue({
          idNumber: id,
          firstName: "",
          lastName: "",
          email: "",
          companyName: "",
          companyEmail: "",
          mobileNumber: "",
          phoneNumber: "",
          postalAddress: "",
          residentialAddress: "",
          dateOfBirth: null,
          guestId: null,
        });
        message.info("No existing guest. Fill details to register.");
      }
    } catch (e) {
      console.error(e);
      message.error("Guest search failed");
    }
  };

  const handleDone = async () => {
    try {
      await form.validateFields([
        "idNumber",
        "firstName",
        "lastName",

        // ✅ Email optional, but validate format if present
        "email",

        "postalAddress",
        "residentialAddress",
        "numberOfAdults",
        "dateOfBirth",
        "mobileNumber",
        "phoneNumber",

        // companyName + companyEmail optional
        "companyName",
        "companyEmail",
      ]);
      onClose?.();
    } catch {
      /* keep open */
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: { controlHeight: 24, controlHeightSM: 20, fontSize: 11, paddingInlineSM: 4 },
          Input: { controlHeight: 24, fontSize: 11, paddingInline: 6, paddingBlock: 2 },
          InputNumber: { controlHeight: 24, fontSize: 11, paddingInline: 6, paddingBlock: 2 },
          DatePicker: { controlHeight: 24, fontSize: 11 },
          Checkbox: { fontSize: 11 },
          Form: { itemMarginBottom: 2, labelFontSize: 11 },
          Select: { controlHeight: 24, fontSize: 11 },
        },
        token: { fontSize: 11, borderRadius: 6, controlHeight: 24, paddingXS: 4, marginXS: 2 },
      }}
    >
      <Drawer
        className="guest-drawer-root"
        title={<span style={{ fontSize: 12, fontWeight: 500 }}>Add Guest</span>}
        width={580}
        open={open}
        onClose={onClose}
        maskClosable={false}
        destroyOnClose={false}
        forceRender
        styles={{
          header: { padding: "6px 8px" },
          body: { padding: 8 },
          footer: { padding: "6px 8px" },
        }}
        extra={
          <Text type="secondary" style={{ fontSize: 11 }}>
            {namePreview || "New guest"}
          </Text>
        }
        footer={
          <div style={{ textAlign: "right" }}>
            <Space size={6}>
              <button className="gd-btn ghost" onClick={onClose}>
                <CloseOutlined /> &nbsp;Close
              </button>
              <button className="gd-btn primary" onClick={handleDone}>
                <CheckOutlined /> &nbsp;Save
              </button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical" size="small" autoComplete="off" style={{ margin: 0 }}>
          {/* ID + DOB */}
          <Row gutter={6} style={{ marginBottom: 2 }}>
            <Col span={14}>
              <Form.Item
                label="ID/Passport"
                name="idNumber"
                rules={[{ required: true, message: "Required" }]}
                preserve
                style={{ marginBottom: 2 }}
              >
                <Input
                  placeholder="77777777777"
                  onPressEnter={handleGuestSearch}
                  suffix={
                    <Tooltip title="Search">
                      <SearchOutlined className="gd-suffix-icon" onClick={handleGuestSearch} />
                    </Tooltip>
                  }
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                label="Date of Birth"
                name="dateOfBirth"
                rules={[{ required: true, message: "Required" }]}
                preserve
                style={{ marginBottom: 2 }}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                  placeholder="DOB"
                  inputReadOnly={false}
                  getPopupContainer={(n) => n?.closest(".ant-drawer") || document.body}
                  popupClassName="dob-popup"
                  disabledDate={(current) => current && current.isAfter(eighteenYearsAgo)}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Names */}
          <Row gutter={6} style={{ marginBottom: 2 }}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[{ required: true, message: "Required" }]}
                preserve
                style={{ marginBottom: 2 }}
              >
                <Input placeholder="First" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[{ required: true, message: "Required" }]}
                preserve
                style={{ marginBottom: 2 }}
              >
                <Input placeholder="Last" />
              </Form.Item>
            </Col>
          </Row>

          {/* Guest Email (OPTIONAL) + Adults */}
          <Row gutter={6} style={{ marginBottom: 2 }}>
            <Col span={16}>
              <Form.Item
                label="Email (optional)"
                name="email"
                rules={[
                  { type: "email", message: "Invalid email" }, // empty allowed
                ]}
                preserve
                style={{ marginBottom: 2 }}
              >
                <Input placeholder="email@example.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Adults"
                name="numberOfAdults"
                initialValue={1}
                rules={[{ required: true, message: "Required" }]}
                preserve
                style={{ marginBottom: 2 }}
              >
                <InputNumber min={1} precision={0} style={{ width: "100%" }} placeholder="1" />
              </Form.Item>
            </Col>
          </Row>

          {/* Company (optional) + Company Email (optional) */}
          <Row gutter={6} style={{ marginBottom: 2 }}>
            <Col span={12}>
              <Form.Item label="Company (optional)" name="companyName" preserve style={{ marginBottom: 2 }}>
                <Input placeholder="Company (optional)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Company Email (optional)"
                name="companyEmail"
                preserve
                style={{ marginBottom: 2 }}
                rules={[{ type: "email", message: "Invalid email" }]} // optional, validates if filled
              >
                <Input placeholder="company@example.com (optional)" />
              </Form.Item>
            </Col>
          </Row>

          {/* Phones (with dial code + flags) */}
          <Row gutter={6} style={{ marginBottom: 2 }}>
            <Col span={12}>
              <Form.Item
                label="Mobile"
                name="mobileNumber"
                preserve
                style={{ marginBottom: 2 }}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, v) => {
                      const phone = getFieldValue("phoneNumber");
                      if (!v && !phone) throw new Error("Mobile or Phone required");
                    },
                  }),
                  {
                    validator: async (_, v) => {
                      if (!v) return;

                      const fallback =
                        COUNTRY_LIST.find((c) => c.dialCode === defaultDialCode) || COUNTRY_LIST[0];
                      const { country, local } = splitE164(v, fallback);

                      if (!validatePhone(country.dialCode, local, country.code)) {
                        throw new Error(`Invalid mobile number for ${country.name}`);
                      }
                    },
                  },
                ]}
              >
                <CountryPhoneInput placeholder="Mobile" defaultDialCode={defaultDialCode} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Phone"
                name="phoneNumber"
                preserve
                style={{ marginBottom: 2 }}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, v) => {
                      const mobile = getFieldValue("mobileNumber");
                      if (!v && !mobile) throw new Error("Mobile or Phone required");
                    },
                  }),
                  {
                    validator: async (_, v) => {
                      if (!v) return;

                      const fallback =
                        COUNTRY_LIST.find((c) => c.dialCode === defaultDialCode) || COUNTRY_LIST[0];
                      const { country, local } = splitE164(v, fallback);

                      if (!validatePhone(country.dialCode, local, country.code)) {
                        throw new Error(`Invalid phone number for ${country.name}`);
                      }
                    },
                  },
                ]}
              >
                <CountryPhoneInput placeholder="Phone" defaultDialCode={defaultDialCode} />
              </Form.Item>
            </Col>
          </Row>

          {/* Addresses: Postal (required) + Residential (required) */}
          <Row gutter={6} style={{ marginBottom: 2 }}>
            <Col span={12}>
              <Form.Item
                label="Postal Address"
                name="postalAddress"
                rules={[{ required: true, message: "Required" }]}
                preserve
                style={{ marginBottom: 2 }}
              >
                <Input.TextArea rows={1} placeholder="Postal address" style={{ resize: "none" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Residential Address"
                name="residentialAddress"
                rules={[{ required: true, message: "Required" }]}
                preserve
                style={{ marginBottom: 2 }}
              >
                <Input.TextArea rows={1} placeholder="Residential address" style={{ resize: "none" }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Kids */}
          <Row gutter={6} style={{ marginBottom: 2 }}>
            <Col span={24}>
              <Form.Item label="Children" preserve style={{ marginBottom: 2 }}>
                <div className="gd-children-inline">
                  <Form.Item name="includesKids" valuePropName="checked" initialValue={false} noStyle preserve>
                    <Checkbox style={{ margin: 0, lineHeight: 1 }}>Includes kids</Checkbox>
                  </Form.Item>
                  <Form.Item name="numberOfKids" initialValue={0} noStyle preserve>
                    <InputNumber min={0} precision={0} disabled={!includesKids} className="gd-kids-number" />
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>
          </Row>

          {/* Payment + Remarks */}
          <Row gutter={6} style={{ marginBottom: 2 }}>
            <Col span={8}>
              <Form.Item label="Paid Amount" name="paidAmount" initialValue={0} preserve style={{ marginBottom: 2 }}>
                <InputNumber min={0} style={{ width: "100%" }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item label="Remarks" name="remarks" preserve style={{ marginBottom: 2 }}>
                <Input.TextArea rows={1} placeholder="Remarks" style={{ resize: "none" }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Notes */}
          <Form.Item label="Notes" name="notes" preserve style={{ marginBottom: 0 }}>
            <Input.TextArea rows={2} placeholder="Notes" style={{ resize: "none" }} />
          </Form.Item>

          {/* hidden id */}
          <Form.Item name="guestId" hidden preserve>
            <Input />
          </Form.Item>
        </Form>
      </Drawer>
    </ConfigProvider>
  );
};

export default GuestDrawer;
